/**
 * PlaylistChildrenManager Component
 * 
 * Comprehensive component for managing playlist content including:
 * - Viewing current playlist items with drag-and-drop reordering
 * - Adding new content items (stories, posts, videos, playlists)
 * - Removing items from the playlist
 * - Content type filtering based on playlist restrictions
 * - Search functionality for finding content to add
 * - Visual feedback for operations
 * 
 * Used in: App.js route /playlists/:id/children
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    Button,
    Alert,
    Grid,
    TextField,
    // MenuItem,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    ListItemSecondaryAction,
    Avatar,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tabs,
    Tab,
    Card,
    CardContent,
    // CardMedia,
    Divider
} from '@mui/material';
import {
    ArrowBack,
    Add,
    Delete,
    // DragIndicator,
    Search,
    // PlayArrow,
    Article,
    VideoLibrary,
    PlaylistPlay,
    Edit
} from '@mui/icons-material';
import { userRequest } from '../../requestMethods';
import PageContainer from '../layout/PageContainer';

const PlaylistChildrenManager = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    
    const [playlist, setPlaylist] = useState(null);
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState({ show: false, message: '', severity: 'info' });
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentTab, setCurrentTab] = useState(0);
    const [availableContent, setAvailableContent] = useState({
        stories: [],
        longPosts: [],
        videos: [],
        playlists: []
    });
    const [contentPagination, setContentPagination] = useState({
        stories: { page: 1, hasMore: true, loading: false },
        longPosts: { page: 1, hasMore: true, loading: false },
        videos: { page: 1, hasMore: true, loading: false },
        playlists: { page: 1, hasMore: true, loading: false }
    });
    const [selectedItems, setSelectedItems] = useState(new Set());
    const [bulkAddLoading, setBulkAddLoading] = useState(false);

    useEffect(() => {
        fetchPlaylist();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const fetchPlaylist = async () => {
        setLoading(true);
        try {
            const response = await userRequest.get(`/contentPlaylist/${id}`);
            setPlaylist(response.data);
        } catch (error) {
            console.error('Error fetching playlist:', error);
            showAlert('Error loading playlist', 'error');
        }
        setLoading(false);
    };

    const fetchAvailableContent = async (resetPagination = true) => {
        if (!playlist) return;

        if (resetPagination) {
            // Reset pagination state when opening dialog
            setContentPagination({
                stories: { page: 1, hasMore: true, loading: false },
                longPosts: { page: 1, hasMore: true, loading: false },
                videos: { page: 1, hasMore: true, loading: false },
                playlists: { page: 1, hasMore: true, loading: false }
            });
            setAvailableContent({ stories: [], longPosts: [], videos: [], playlists: [] });
            setSelectedItems(new Set());
        }

        await loadContentPage();
    };

    const loadContentPage = async (contentTypeKey = null) => {
        if (!playlist) return;

        try {
            const contentTypes = getAvailableContentTypes();
            const typeMapping = {
                'stories': { type: 'ContentShortStory', endpoint: '/contentShortStory', responseKey: 'stories' },
                'longPosts': { type: 'ContentLongPost', endpoint: '/contentLongPost', responseKey: 'longPosts' },
                'videos': { type: 'ContentVideoPost', endpoint: '/contentVideoPost', responseKey: 'videoPosts' },
                'playlists': { type: 'ContentPlaylist', endpoint: '/contentPlaylist', responseKey: 'playlists' }
            };

            const typesToLoad = contentTypeKey ? [contentTypeKey] : Object.keys(typeMapping);

            for (const key of typesToLoad) {
                const mapping = typeMapping[key];
                if (!contentTypes.includes(mapping.type)) continue;

                const paginationState = contentPagination[key];
                if (!paginationState.hasMore || paginationState.loading) continue;

                // Set loading state
                setContentPagination(prev => ({
                    ...prev,
                    [key]: { ...prev[key], loading: true }
                }));

                try {
                    const params = { 
                        page: paginationState.page, 
                        limit: 20 
                    };

                    // Filter out current playlist from playlists
                    if (key === 'playlists') {
                        params.exclude = id;
                    }

                    const response = await userRequest.get(mapping.endpoint, { params });
                    const newData = response.data[mapping.responseKey] || [];
                    const filteredData = key === 'playlists' 
                        ? newData.filter(p => p._id !== id) 
                        : newData;

                    setAvailableContent(prev => ({
                        ...prev,
                        [key]: paginationState.page === 1 
                            ? filteredData 
                            : [...prev[key], ...filteredData]
                    }));

                    // Update pagination state
                    setContentPagination(prev => ({
                        ...prev,
                        [key]: {
                            ...prev[key],
                            page: prev[key].page + 1,
                            hasMore: filteredData.length === params.limit,
                            loading: false
                        }
                    }));

                } catch (error) {
                    console.error(`Error loading ${key}:`, error);
                    setContentPagination(prev => ({
                        ...prev,
                        [key]: { ...prev[key], loading: false }
                    }));
                }
            }
        } catch (error) {
            console.error('Error fetching available content:', error);
            showAlert('Error loading available content', 'error');
        }
    };

    const getAvailableContentTypes = () => {
        if (!playlist) return [];

        const typeMapping = {
            'video': ['ContentVideoPost'],
            'text': ['ContentShortStory', 'ContentLongPost'],
            'playlist': ['ContentPlaylist'],
            'mixed': ['ContentShortStory', 'ContentLongPost', 'ContentVideoPost', 'ContentPlaylist']
        };

        return typeMapping[playlist.contentType] || [];
    };

    const showAlert = (message, severity = 'info') => {
        setAlert({ show: true, message, severity });
        setTimeout(() => setAlert({ show: false, message: '', severity: 'info' }), 5000);
    };

    const handleBulkAddContent = async () => {
        if (selectedItems.size === 0) {
            showAlert('Please select items to add', 'warning');
            return;
        }

        setBulkAddLoading(true);
        
        try {
            // Group selected items by content type
            // const itemsByType = {};
            const availableTabs = getAvailableTabs();
            const currentTabData = availableTabs[currentTab];
            
            if (!currentTabData) return;
            
            const contentTypeMapping = {
                'stories': 'ContentShortStory',
                'longPosts': 'ContentLongPost', 
                'videos': 'ContentVideoPost',
                'playlists': 'ContentPlaylist'
            };
            
            const contentType = contentTypeMapping[currentTabData.key];
            const selectedItemsArray = Array.from(selectedItems);
            
            const response = await userRequest.post(`/contentPlaylist/${id}/children`, {
                contentType,
                contentIds: selectedItemsArray
            });
            
            const { results } = response.data;
            
            // Show detailed results
            if (results.added.length > 0) {
                showAlert(`Successfully added ${results.added.length} items to playlist`, 'success');
            }
            
            if (results.skipped.length > 0) {
                showAlert(`${results.skipped.length} items were skipped (already in playlist)`, 'info');
            }
            
            if (results.errors.length > 0) {
                showAlert(`${results.errors.length} items failed to add`, 'error');
            }
            
            // Clear selection and refresh
            setSelectedItems(new Set());
            fetchPlaylist();
            
            // If all items were added or skipped, close dialog
            if (results.errors.length === 0) {
                setAddDialogOpen(false);
            }
            
        } catch (error) {
            console.error('Error adding content:', error);
            showAlert(error.response?.data?.error || 'Error adding content', 'error');
        }
        
        setBulkAddLoading(false);
    };

    const handleItemSelection = (contentId, isSelected) => {
        setSelectedItems(prev => {
            const newSet = new Set(prev);
            if (isSelected) {
                newSet.add(contentId);
            } else {
                newSet.delete(contentId);
            }
            return newSet;
        });
    };

    const handleSelectAll = (contentTypeKey) => {
        const content = availableContent[contentTypeKey] || [];
        const allIds = content
            .filter(item => !isItemAlreadyAdded(item._id))
            .map(item => item._id);
        
        setSelectedItems(prev => {
            const newSet = new Set(prev);
            allIds.forEach(id => newSet.add(id));
            return newSet;
        });
    };

    const handleDeselectAll = () => {
        setSelectedItems(new Set());
    };

    const isItemAlreadyAdded = (contentId) => {
        return playlist?.children?.some(child => child.contentId === contentId);
    };

    const handleRemoveContent = async (contentId) => {
        if (window.confirm('Are you sure you want to remove this item from the playlist?')) {
            try {
                await userRequest.delete(`/contentPlaylist/${id}/children/${contentId}`);
                showAlert('Content removed from playlist', 'success');
                fetchPlaylist();
            } catch (error) {
                console.error('Error removing content:', error);
                showAlert('Error removing content', 'error');
            }
        }
    };

    // const handleReorderContent = async (newOrder) => {
    //     try {
    //         await userRequest.patch(`/contentPlaylist/${id}/children/reorder`, {
    //             childrenOrder: newOrder
    //         });
    //         
    //         showAlert('Playlist order updated', 'success');
    //         fetchPlaylist();
    //     } catch (error) {
    //         console.error('Error reordering content:', error);
    //         showAlert('Error updating order', 'error');
    //     }
    // };

    const openAddDialog = () => {
        fetchAvailableContent();
        setAddDialogOpen(true);
    };

    const getContentIcon = (contentType) => {
        const icons = {
            'ContentShortStory': <Article />,
            'ContentLongPost': <Article />,
            'ContentVideoPost': <VideoLibrary />,
            'ContentPlaylist': <PlaylistPlay />
        };
        return icons[contentType] || <Article />;
    };

    const getContentTypeLabel = (contentType) => {
        const labels = {
            'ContentShortStory': 'Short Story',
            'ContentLongPost': 'Long Post',
            'ContentVideoPost': 'Video',
            'ContentPlaylist': 'Playlist'
        };
        return labels[contentType] || contentType;
    };

    const renderContentItem = (content, contentType) => {
        const isAlreadyAdded = isItemAlreadyAdded(content._id);
        const isSelected = selectedItems.has(content._id);

        return (
            <Card key={content._id} sx={{ mb: 2 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                            src={content.thumbUrl}
                            alt={content.title}
                            variant="rounded"
                            sx={{ width: 60, height: 40 }}
                        >
                            {getContentIcon(contentType)}
                        </Avatar>
                        
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="subtitle1">
                                {content.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {getContentTypeLabel(contentType)}
                                {content.author && ` • by ${content.author}`}
                            </Typography>
                        </Box>

                        {isAlreadyAdded ? (
                            <Chip 
                                label="Added" 
                                size="small" 
                                color="success" 
                                variant="outlined" 
                            />
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={(e) => handleItemSelection(content._id, e.target.checked)}
                                    style={{ 
                                        marginRight: '8px',
                                        transform: 'scale(1.2)',
                                        accentColor: '#1976d2'
                                    }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                    Select
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </CardContent>
            </Card>
        );
    };

    const getTabContent = () => {
        const tabs = [
            { label: 'Stories', key: 'stories', contentType: 'ContentShortStory' },
            { label: 'Long Posts', key: 'longPosts', contentType: 'ContentLongPost' },
            { label: 'Videos', key: 'videos', contentType: 'ContentVideoPost' },
            { label: 'Playlists', key: 'playlists', contentType: 'ContentPlaylist' }
        ];

        const availableContentTypes = getAvailableContentTypes();
        const filteredTabs = tabs.filter(tab => availableContentTypes.includes(tab.contentType));

        if (currentTab >= filteredTabs.length) {
            return null;
        }

        const currentTabData = filteredTabs[currentTab];
        const content = availableContent[currentTabData.key] || [];
        const paginationState = contentPagination[currentTabData.key];
        
        const filteredContent = content.filter(item =>
            item.title.toLowerCase().includes(searchQuery.toLowerCase())
        );

        const availableItems = filteredContent.filter(item => !isItemAlreadyAdded(item._id));
        const selectedCount = Array.from(selectedItems).filter(id => 
            availableItems.some(item => item._id === id)
        ).length;

        return (
            <Box>
                {/* Selection Controls */}
                {availableItems.length > 0 && (
                    <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                {selectedCount} of {availableItems.length} items selected
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    size="small"
                                    onClick={() => handleSelectAll(currentTabData.key)}
                                    disabled={selectedCount === availableItems.length}
                                >
                                    Select All
                                </Button>
                                <Button
                                    size="small"
                                    onClick={handleDeselectAll}
                                    disabled={selectedCount === 0}
                                >
                                    Deselect All
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                )}

                {/* Content List */}
                {filteredContent.length === 0 && !paginationState.loading ? (
                    <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                        No {currentTabData.label.toLowerCase()} found
                    </Typography>
                ) : (
                    <>
                        {filteredContent.map(item => renderContentItem(item, currentTabData.contentType))}
                        
                        {/* Load More Button */}
                        {paginationState.hasMore && (
                            <Box sx={{ textAlign: 'center', mt: 2 }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => loadContentPage(currentTabData.key)}
                                    disabled={paginationState.loading}
                                >
                                    {paginationState.loading ? 'Loading...' : 'Load More'}
                                </Button>
                            </Box>
                        )}

                        {/* Loading indicator */}
                        {paginationState.loading && filteredContent.length === 0 && (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <Typography color="text.secondary">
                                    Loading {currentTabData.label.toLowerCase()}...
                                </Typography>
                            </Box>
                        )}
                    </>
                )}
            </Box>
        );
    };

    const getAvailableTabs = () => {
        const tabs = [
            { label: 'Stories', key: 'stories', contentType: 'ContentShortStory' },
            { label: 'Long Posts', key: 'longPosts', contentType: 'ContentLongPost' },
            { label: 'Videos', key: 'videos', contentType: 'ContentVideoPost' },
            { label: 'Playlists', key: 'playlists', contentType: 'ContentPlaylist' }
        ];

        const availableContentTypes = getAvailableContentTypes();
        return tabs.filter(tab => availableContentTypes.includes(tab.contentType));
    };

    if (loading) {
        return (
            <PageContainer title="Loading...">
                <Typography>Loading playlist...</Typography>
            </PageContainer>
        );
    }

    if (!playlist) {
        return (
            <PageContainer title="Playlist Not Found">
                <Alert severity="error">
                    Playlist not found or you don't have permission to access it.
                </Alert>
            </PageContainer>
        );
    }

    return (
        <PageContainer title={`Manage: ${playlist.title}`}>
            {alert.show && (
                <Alert severity={alert.severity} sx={{ mb: 2 }}>
                    {alert.message}
                </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Button
                    startIcon={<ArrowBack />}
                    onClick={() => navigate('/playlists')}
                >
                    Back to Playlists
                </Button>

                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={openAddDialog}
                >
                    Add Content
                </Button>
            </Box>

            {/* Playlist Info */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                        <Typography variant="h5" gutterBottom>
                            {playlist.title}
                        </Typography>
                        {playlist.description && (
                            <Typography color="text.secondary" paragraph>
                                {playlist.description}
                            </Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                            <Chip
                                label={getContentTypeLabel(playlist.contentType)}
                                color="primary"
                                variant="outlined"
                            />
                            <Chip
                                label={`${playlist.totalItems || 0} items`}
                                variant="outlined"
                            />
                        </Box>
                    </Grid>
                    {playlist.thumbUrl && (
                        <Grid item xs={12} md={4}>
                            <Avatar
                                src={playlist.thumbUrl}
                                alt={playlist.title}
                                variant="rounded"
                                sx={{ width: '100%', height: 120 }}
                            />
                        </Grid>
                    )}
                </Grid>
            </Paper>

            {/* Current Playlist Items */}
            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Playlist Items ({playlist.children?.length || 0})
                </Typography>

                {!playlist.children || playlist.children.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary">
                            No items in this playlist yet
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={openAddDialog}
                            sx={{ mt: 2 }}
                        >
                            Add Your First Item
                        </Button>
                    </Box>
                ) : (
                    <List>
                        {playlist.children.map((child, index) => (
                            <React.Fragment key={`${child.contentType}-${child.contentId}`}>
                                <ListItem>
                                    <ListItemAvatar>
                                        <Avatar
                                            src={child.content?.thumbUrl}
                                            variant="rounded"
                                        >
                                            {getContentIcon(child.contentType)}
                                        </Avatar>
                                    </ListItemAvatar>
                                    
                                    <ListItemText
                                        primary={child.content?.title || 'Unknown Title'}
                                        secondary={
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    {getContentTypeLabel(child.contentType)}
                                                    {child.content?.author && ` • by ${child.content.author}`}
                                                    <Chip 
                                                        size="small" 
                                                        label={child.content.isReadyToServe ? 'Published' : 'Draft'}
                                                        color={child.content.isReadyToServe ? 'success' : 'warning'}
                                                        variant="outlined"
                                                        sx={{ ml: 1 }}
                                                    />
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                    
                                    <ListItemSecondaryAction>
                                        {child.contentType === 'ContentVideoPost' && (
                                            <Button
                                                edge="end"
                                                onClick={() => navigate(`/video-posts/edit/${child.contentId}`)}
                                                color="primary"
                                                sx={{ mr: 1 }}
                                                title="Edit Video Post"
                                            >
                                                Edit Transcript
                                            </Button>
                                        )}
                                        <IconButton
                                            edge="end"
                                            onClick={() => handleRemoveContent(child.contentId)}
                                            color="error"
                                        >
                                            <Delete />
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
                                
                                {index < playlist.children.length - 1 && <Divider />}
                            </React.Fragment>
                        ))}
                    </List>
                )}
            </Paper>

            {/* Add Content Dialog */}
            <Dialog
                open={addDialogOpen}
                onClose={() => setAddDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Add Content to Playlist</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        placeholder="Search content..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                        sx={{ mb: 2 }}
                    />

                    <Tabs
                        value={currentTab}
                        onChange={(e, newValue) => setCurrentTab(newValue)}
                        sx={{ mb: 2 }}
                    >
                        {getAvailableTabs().map((tab, index) => (
                            <Tab key={tab.key} label={tab.label} />
                        ))}
                    </Tabs>

                    <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                        {getTabContent()}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            {selectedItems.size} items selected
                        </Typography>
                        
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button onClick={() => setAddDialogOpen(false)}>
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleBulkAddContent}
                                disabled={selectedItems.size === 0 || bulkAddLoading}
                                startIcon={bulkAddLoading ? null : <Add />}
                            >
                                {bulkAddLoading 
                                    ? 'Adding...' 
                                    : `Add Selected (${selectedItems.size})`
                                }
                            </Button>
                        </Box>
                    </Box>
                </DialogActions>
            </Dialog>
        </PageContainer>
    );
};

export default PlaylistChildrenManager;